import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { apiClient } from '@services/api'

interface FgBatchRow {
  batchNo: string
  packets: number
  producedDate: string
  expiryDate: string
  daysToExpiry: number
  unitCost: number
  source: string
  lastSoldDate: string | null
}
interface FgRow {
  product: string
  packG: number
  unit: string
  sourceKind: 'repacked' | 'purchased'
  addedToday: number
  onHand: number
  reorderPoint: number
  status: 'OK' | 'Low'
  value: number
  rawAvailableKg: number
  batches: FgBatchRow[]
}
interface RawBatchRow {
  batchNo: string
  billNo: string
  supplierName: string
  receivedDate: string
  receivedKg: number
  availableKg: number
  unitCost: number
  ageDays: number
}
interface RawRow {
  product: string
  unit: string
  availableKg: number
  receivedKg: number
  value: number
  batches: RawBatchRow[]
}
interface RepackedToday {
  sessionNo: string
  product: string
  enteredBy: string
  createdAt: string
  totalTakeKg: number
  outputs: Array<{ packG: number; packets: number; batchNo: string }>
  packets: number
}
interface StockAlert {
  batchNo: string
  product: string
  packG: number
  kind: 'expiry' | 'stagnant'
  producedDate: string
  expiryDate: string
  daysToExpiry: number
  daysSinceSold: number
  packets: number
  valueAtRisk: number
}
interface Overview {
  kpis: {
    skusBelowReorder: number
    totalPackets: number
    packetsAddedToday: number
    finishedGoodsValue: number
    sessionsToday: number
    draftsToday: number
    rawProducts: number
    rawAvailableKg: number
    rawReceivedTodayKg: number
    repackedTodayPackets: number
  }
  segments: Array<{ key: string; segment: string; value: number; pct: number }>
  totalValue: number
  finishedGoods: FgRow[]
  rawMaterials: RawRow[]
  repackedToday: RepackedToday[]
  alerts: StockAlert[]
}

const fmtTime = (iso: string) => new Date(iso).toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' }).toUpperCase()

const inr = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`
const lakh = (n: number) => (n >= 100000 ? `₹${(n / 100000).toFixed(1).replace(/\.0$/, '')}L` : inr(n))
const packLabel = (g: number) => (g >= 1000 ? `${g / 1000} kg` : `${g} g`)
const shortPack = (g: number) => (g >= 1000 ? `${g / 1000}kg` : `${g}g`)
const shortName = (p: string) => p.replace(/\s*\(.*?\)\s*/g, '')
const fmtDay = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
const kg1 = (n: number) => `${n.toLocaleString('en-IN', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} kg`
const unitOfRow = (r: FgRow) => (r.packG === 0 ? r.unit : 'pkt')
const countLabel = (n: number, unit: string) => `${n.toLocaleString('en-IN')} ${unit}`

export function StockForSale() {
  const [openRow, setOpenRow] = useState<string | null>(null)
  const [openRaw, setOpenRaw] = useState<string | null>(null)
  const [filter, setFilter] = useState<'all' | 'repacked' | 'purchased' | 'low'>('all')
  const { data, isLoading } = useQuery({ queryKey: ['stock-overview'], queryFn: () => apiClient.get<any>('/stock/overview'), refetchInterval: 60000 })
  const { data: productsData } = useQuery({ queryKey: ['products-list'], queryFn: () => apiClient.get<any>('/products') })
  const tamilOf = useMemo(() => { const m = new Map<string, string>(); for (const p of productsData?.data?.items || []) if (p.nameTamil) m.set(p.name, p.nameTamil); return (name: string) => m.get(name) || null }, [productsData])
  const ov: Overview | undefined = data?.data
  const k = ov?.kpis
  const rows = (ov?.finishedGoods || []).filter((r) =>
    filter === 'all' ? true : filter === 'low' ? r.status === 'Low' : r.sourceKind === filter
  )
  const raws = ov?.rawMaterials || []
  const repacked = ov?.repackedToday || []
  const unitFor = (product: string, packG: number) => {
    const r = ov?.finishedGoods.find((x) => x.product === product && x.packG === packG)
    return r ? unitOfRow(r) : 'pkt'
  }

  return (
    <div>
      <div className="hdr">
        <h1>Stock for Sale</h1>
        <p>Finished goods available for billing, updated immediately on purchase entry or repacking submission — no approval delay.</p>
      </div>

      <div className="kpis">
        <div className="kpi">
          <div className="label">SKUs Below Reorder</div>
          <div className="val">{k ? k.skusBelowReorder : '—'}</div>
          <div className={`sub ${k && k.skusBelowReorder > 0 ? 'down' : 'up'}`}>{k ? (k.skusBelowReorder > 0 ? 'Needs restock' : 'All above reorder point') : ' '}</div>
        </div>
        <div className="kpi">
          <div className="label">Total Finished Packets</div>
          <div className="val">{k ? k.totalPackets.toLocaleString('en-IN') : '—'}</div>
          <div className={`sub ${k && k.packetsAddedToday > 0 ? 'up' : ''}`}>{k ? (k.packetsAddedToday > 0 ? `+${k.packetsAddedToday.toLocaleString('en-IN')} today` : 'No additions today yet') : ' '}</div>
        </div>
        <div className="kpi">
          <div className="label">Stock Value</div>
          <div className="val">{k ? lakh(k.finishedGoodsValue) : '—'}</div>
          <div className="sub">At cost</div>
        </div>
        <div className="kpi">
          <div className="label">Repacking Sessions Today</div>
          <div className="val">{k ? k.sessionsToday : '—'}</div>
          <div className={`sub ${k && k.draftsToday > 0 ? 'down' : 'up'}`}>{k ? (k.draftsToday > 0 ? `${k.draftsToday} draft${k.draftsToday === 1 ? '' : 's'} waiting` : 'All active, no queue') : ' '}</div>
        </div>
      </div>

      <div className="card mb-[18px]">
        <h2>Stocks in Trade — Value by Segment</h2>
        <table>
          <thead>
            <tr>
              <th>Segment</th>
              <th className="num">Value</th>
              <th className="num">% of Total</th>
            </tr>
          </thead>
          <tbody>
            {(ov?.segments || []).map((s) => (
              <tr key={s.key}>
                <td>{s.segment}</td>
                <td className="num">{lakh(s.value)}</td>
                <td className="num">{s.pct}%</td>
              </tr>
            ))}
            {ov && (
              <tr>
                <td>
                  <b>Total Stock Value</b>
                </td>
                <td className="num">
                  <b>{lakh(ov.totalValue)}</b>
                </td>
                <td className="num">
                  <b>100%</b>
                </td>
              </tr>
            )}
            {isLoading && (
              <tr>
                <td colSpan={3} className="text-center text-ink-soft">
                  Loading…
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <div className="hint">
          Direct-sale items (biscuits, soap) count straight into Finished Goods on purchase entry — they never pass through the Raw
          Material or In Repacking segments.
        </div>
      </div>

      <div className="card mb-[18px]">
        <div className="field-label-row">
          <h2 className="!m-0">
            Raw Material — Awaiting Repacking{' '}
            <small>
              — bulk purchases by kg{k ? ` · ${k.rawProducts} product${k.rawProducts === 1 ? '' : 's'} · ${kg1(k.rawAvailableKg)} available` : ''}
              {k && k.rawReceivedTodayKg > 0 ? ` · +${kg1(k.rawReceivedTodayKg)} received today` : ''}
            </small>
          </h2>
          <Link to="/dashboard/repacking" className="btn-ghost btn-xs">
            Repacking Entry →
          </Link>
        </div>
        <table className="mt-[10px]">
          <thead>
            <tr>
              <th>Product</th>
              <th>Unit</th>
              <th className="num">Received</th>
              <th className="num">Available</th>
              <th className="num">Batches</th>
              <th className="num">Value at cost</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {raws.length === 0 && (
              <tr>
                <td colSpan={7} className="text-center text-ink-soft">
                  {isLoading ? 'Loading…' : 'No bulk stock waiting — everything purchased by kg has been repacked.'}
                </td>
              </tr>
            )}
            {raws.map((r) => {
              const open = openRaw === r.product
              return (
                <RawRows key={r.product} row={r} tamil={tamilOf(r.product)} open={open} onToggle={() => setOpenRaw(open ? null : r.product)} />
              )
            })}
          </tbody>
        </table>
        <div className="hint">
          Every purchase line bought by kg lands here the moment the bill is submitted; it moves to Finished Goods below once it's repacked
          into packets. Click a row to see its batches.
        </div>
      </div>

      <div className="card mb-[18px]" data-testid="repacked-today">
        <div className="field-label-row">
          <h2 className="!m-0">
            Repacked Today — Added to Stock for Sale{' '}
            <small>
              {k ? `— ${repacked.length} session${repacked.length === 1 ? '' : 's'} · ${k.repackedTodayPackets.toLocaleString('en-IN')} packets` : ''}
            </small>
          </h2>
          <Link to="/dashboard/repacking" className="btn-ghost btn-xs">
            Repacking Entry →
          </Link>
        </div>
        {repacked.length === 0 && <div className="text-center py-5 text-ink-soft text-[13px]">No repacking submitted today yet — packets appear here the moment a session is submitted.</div>}
        {repacked.map((s) => (
          <div key={s.sessionNo} className="queue-item">
            <div>
              <b>{shortName(s.product)}</b> — {s.outputs.map((o) => `${shortPack(o.packG)} × ${o.packets.toLocaleString('en-IN')}`).join(', ')}
              <div className="qmeta">
                {s.sessionNo} · {kg1(s.totalTakeKg)} raw used
                {s.outputs.some((o) => o.batchNo) ? ` · batches ${s.outputs.map((o) => o.batchNo).filter(Boolean).join(', ')}` : ''} · Entered by {s.enteredBy} · {fmtTime(s.createdAt)}
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-[15px] font-bold text-good">+{s.packets.toLocaleString('en-IN')} pkt</div>
              <span className="badge badge-full">In stock</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid2">
        <div className="card">
          <div className="field-label-row">
            <h2 className="!m-0">Finished Goods — All Pack Sizes</h2>
            <div className="flex gap-1 flex-wrap justify-end">
              <button type="button" className={`chip ${filter === 'all' ? 'on' : ''}`} onClick={() => setFilter('all')}>
                All
              </button>
              <button type="button" className={`chip ${filter === 'repacked' ? 'on' : ''}`} onClick={() => setFilter('repacked')}>
                Repacked
              </button>
              <button type="button" className={`chip ${filter === 'purchased' ? 'on' : ''}`} onClick={() => setFilter('purchased')}>
                Purchased as-is
              </button>
              <button type="button" className={`chip danger-chip ${filter === 'low' ? 'on' : ''}`} onClick={() => setFilter('low')}>
                Below reorder
              </button>
            </div>
          </div>
          <table className="mt-[10px]">
            <thead>
              <tr>
                <th>Product</th>
                <th>Pack Size</th>
                <th>Unit</th>
                <th>Source</th>
                <th className="num">On Hand</th>
                <th className="num">Reorder Point</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-center text-ink-soft">
                    {isLoading ? 'Loading…' : filter === 'low' ? 'Nothing below reorder point.' : filter === 'repacked' ? 'Nothing repacked yet.' : filter === 'purchased' ? 'No items bought as-is yet.' : 'No finished goods yet.'}
                  </td>
                </tr>
              )}
              {rows.map((r) => {
                const key = `${r.product}|${r.packG}`
                const open = openRow === key
                return (
                  <FgRows key={key} row={r} tamil={tamilOf(r.product)} open={open} onToggle={() => setOpenRow(open ? null : key)} />
                )
              })}
            </tbody>
          </table>
          <div className="hint">Click a row to see its batches in FEFO order (soonest expiry first) — the order billing will sell them in.</div>
        </div>

        <div className="card">
          <h2>
            Stock Clearance Alerts <small>— ranked by ₹ at risk</small>
          </h2>
          {ov && ov.alerts.length === 0 && <div className="text-center py-6 text-ink-soft text-[13px]">Nothing near expiry or stagnant — stock is turning over.</div>}
          {(ov?.alerts || []).map((a) => (
            <div key={a.batchNo} className="alert-item">
              <div className="left">
                <b>
                  {shortName(a.product)}
                  {a.packG > 0 ? ` ${shortPack(a.packG)}` : ''} · Batch {a.batchNo}
                </b>
                <div className="qmeta">
                  {a.kind === 'expiry'
                    ? `Produced ${fmtDay(a.producedDate)} · ${a.daysToExpiry < 0 ? `expired ${Math.abs(a.daysToExpiry)} days ago` : a.daysToExpiry === 0 ? 'expires today' : `expires in ${a.daysToExpiry} days`} · ${countLabel(a.packets, unitFor(a.product, a.packG))}`
                    : `Not sold in ${a.daysSinceSold} days · ${countLabel(a.packets, unitFor(a.product, a.packG))}`}
                </div>
              </div>
              <div className="text-right">
                <div className={`tag ${a.kind === 'expiry' ? 'expiry' : 'stagnant'}`}>{a.kind === 'expiry' ? (a.daysToExpiry < 0 ? 'Expired' : 'Near Expiry') : 'Stagnant'}</div>
                <div className="qmeta mt-1">{inr(a.valueAtRisk)} at risk</div>
              </div>
            </div>
          ))}
          <div className="hint">Billing auto-sells the soonest-expiring batch first (FEFO) — these are what's still sitting despite that.</div>
          <div className="flex gap-2 mt-3">
            <Link to="/dashboard/sales/invoices" className="btn-ghost btn-xs">
              Bill clearance stock →
            </Link>
            <Link to="/dashboard/repacking" className="btn-ghost btn-xs">
              Repacking Entry →
            </Link>
          </div>
        </div>
      </div>
    </div>
  )
}

function RawRows({ row, tamil, open, onToggle }: { row: RawRow; tamil?: string | null; open: boolean; onToggle: () => void }) {
  return (
    <>
      <tr className="cursor-pointer hover:bg-[#FAFAF7]" onClick={onToggle}>
        <td>
          <span className="text-ink-soft text-[10px] mr-1">{open ? '▾' : '▸'}</span>
          {row.product}
          {tamil && <div className="text-[11px] text-ink-soft ml-[14px]">{tamil}</div>}
        </td>
        <td>{row.unit}</td>
        <td className="num">{kg1(row.receivedKg)}</td>
        <td className="num font-semibold">{kg1(row.availableKg)}</td>
        <td className="num">{row.batches.length}</td>
        <td className="num">{inr(row.value)}</td>
        <td className="text-right">
          <Link to={`/dashboard/repacking?product=${encodeURIComponent(row.product)}`} className="btn-ghost btn-xs" onClick={(e) => e.stopPropagation()}>
            Repack →
          </Link>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="!p-0 bg-[#FAFAF7]">
            <table className="text-[12px]">
              <thead>
                <tr>
                  <th>Batch</th>
                  <th>From bill</th>
                  <th>Supplier</th>
                  <th className="num">Received</th>
                  <th className="num">Age</th>
                  <th className="num">Received kg</th>
                  <th className="num">Available kg</th>
                  <th className="num">Cost / kg</th>
                </tr>
              </thead>
              <tbody>
                {row.batches.map((b, i) => (
                  <tr key={b.batchNo}>
                    <td>
                      {b.batchNo}
                      {i === 0 && row.batches.length > 1 && <span className="badge badge-ok ml-1">Oldest</span>}
                    </td>
                    <td>
                      <Link to="/dashboard/procurement/purchase-bills/list" className="text-primary-500 hover:underline">
                        {b.billNo}
                      </Link>
                    </td>
                    <td className="text-ink-soft">{b.supplierName}</td>
                    <td className="num">{fmtDay(b.receivedDate)}</td>
                    <td className="num">{b.ageDays}d</td>
                    <td className="num">{kg1(b.receivedKg)}</td>
                    <td className="num font-semibold">{kg1(b.availableKg)}</td>
                    <td className="num">{inr(b.unitCost)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  )
}

function FgRows({ row, tamil, open, onToggle }: { row: FgRow; tamil?: string | null; open: boolean; onToggle: () => void }) {
  const unit = unitOfRow(row)
  return (
    <>
      <tr className="cursor-pointer hover:bg-[#FAFAF7]" onClick={onToggle}>
        <td>
          <span className="text-ink-soft text-[10px] mr-1">{open ? '▾' : '▸'}</span>
          {shortName(row.product)}
          {tamil && <div className="text-[11px] text-ink-soft ml-[14px]">{tamil}</div>}
        </td>
        <td>{row.packG === 0 ? 'As purchased' : packLabel(row.packG)}</td>
        <td>{unit}</td>
        <td>
          <span className={`badge ${row.sourceKind === 'repacked' ? 'badge-pending' : 'badge-draft'}`}>{row.sourceKind === 'repacked' ? 'Repacked' : 'Purchased'}</span>
          {row.addedToday > 0 && <div className="text-[10.5px] text-good mt-[2px]">+{row.addedToday.toLocaleString('en-IN')} today</div>}
        </td>
        <td className="num">{row.onHand.toLocaleString('en-IN')}</td>
        <td className="num">{row.reorderPoint.toLocaleString('en-IN')}</td>
        <td>
          <span className={`badge ${row.status === 'Low' ? 'badge-low' : 'badge-ok'}`}>{row.status}</span>
        </td>
      </tr>
      {open && (
        <tr>
          <td colSpan={7} className="!p-0 bg-[#FAFAF7]">
            <table className="text-[12px]">
              <thead>
                <tr>
                  <th>Batch</th>
                  <th>Source</th>
                  <th className="num">Produced</th>
                  <th className="num">Expires</th>
                  <th className="num">{unit === 'pkt' ? 'Packets' : unit}</th>
                  <th className="num">Cost / {unit}</th>
                  <th className="num">Value</th>
                </tr>
              </thead>
              <tbody>
                {row.batches.map((b, i) => (
                  <tr key={b.batchNo}>
                    <td>
                      {b.batchNo}
                      {i === 0 && row.batches.length > 1 && <span className="badge badge-ok ml-1">Sells first</span>}
                    </td>
                    <td className="text-ink-soft">{b.source}</td>
                    <td className="num">{fmtDay(b.producedDate)}</td>
                    <td className={`num ${b.daysToExpiry <= 30 ? 'text-danger font-semibold' : ''}`}>
                      {fmtDay(b.expiryDate)} <span className="text-ink-soft">({b.daysToExpiry}d)</span>
                    </td>
                    <td className="num">{b.packets.toLocaleString('en-IN')}</td>
                    <td className="num">{inr(b.unitCost)}</td>
                    <td className="num">{inr(b.packets * b.unitCost)}</td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={6} className="text-right font-semibold">
                    Value at cost
                  </td>
                  <td className="num font-semibold">{inr(row.value)}</td>
                </tr>
                <tr>
                  <td colSpan={7} className="!py-2">
                    <div className="flex flex-wrap gap-2 items-center text-[11.5px] text-ink-soft" data-testid="row-actions">
                      {row.status === 'Low' && <span>Short by {countLabel(row.reorderPoint - row.onHand, unit)} to reach the reorder point.</span>}
                      {row.packG > 0 &&
                        (row.rawAvailableKg > 0 ? (
                          <Link to={`/dashboard/repacking?product=${encodeURIComponent(row.product)}`} className="btn-ghost btn-xs">
                            Repack more → ({row.rawAvailableKg} kg raw available)
                          </Link>
                        ) : (
                          <span>No raw stock left to repack.</span>
                        ))}
                      <Link to={`/dashboard/procurement/purchase-bills?product=${encodeURIComponent(row.product)}`} className="btn-ghost btn-xs">
                        Buy more →
                      </Link>
                      {row.onHand > 0 && (
                        <Link to={`/dashboard/sales/invoices?item=${encodeURIComponent(`${row.product}|${row.packG}`)}`} className="btn-ghost btn-xs">
                          Bill this item →
                        </Link>
                      )}
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  )
}
